/**
 * As conexões aos bancos dos clientes, sem React e sem rede.
 *
 * A senha é o único segredo: vai ao serviço ao salvar, fica selada no cofre
 * dele e nunca volta. Editar sem trocar a senha não a manda.
 */

import type { ConexaoDaApi, CriarConexao, EditarConexao } from '@cliqcrm/contracts';
import type { TomSelo } from '@/shared/ui';

export type Conexao = ConexaoDaApi;
export type Familia = Conexao['familia'];

export const TIPOS_DE_BANCO: Record<Familia, { rotulo: string; sigla: string; porta: number }> = {
  postgres: { rotulo: 'PostgreSQL', sigla: 'PG', porta: 5432 },
  mysql: { rotulo: 'MySQL / MariaDB', sigla: 'My', porta: 3306 },
  sqlserver: { rotulo: 'SQL Server', sigla: 'MS', porta: 1433 },
};

export const SITUACAO_DA_CONEXAO: Record<Conexao['situacao'], { rotulo: string; tom: TomSelo }> = {
  ok: { rotulo: 'Conectado', tom: 'ok' },
  falhou: { rotulo: 'Falhou', tom: 'erro' },
  nao_testada: { rotulo: 'Nunca testada', tom: 'rascunho' },
};

export const CENARIO: Record<Conexao['cenario'], string> = {
  direto: 'Direto, pela internet',
  gateway: 'Por um gateway na rede do cliente',
};

export type Formulario = {
  nome: string;
  familia: Familia;
  host: string;
  porta: string;
  banco: string;
  usuario: string;
  senha: string;
  cenario: Conexao['cenario'];
};

export function formularioVazio(): Formulario {
  return {
    nome: '',
    familia: 'postgres',
    host: '',
    porta: String(TIPOS_DE_BANCO.postgres.porta),
    banco: '',
    usuario: '',
    senha: '',
    cenario: 'direto',
  };
}

export function formularioDe(c: Conexao): Formulario {
  return {
    nome: c.nome,
    familia: c.familia,
    host: c.host,
    porta: String(c.porta),
    banco: c.banco,
    usuario: c.usuario,
    senha: '',
    cenario: c.cenario,
  };
}

/**
 * Trocar o tipo põe a porta padrão dele, a menos que a pessoa tenha digitado
 * outra: a porta vazia ou a padrão do tipo anterior muda; a escolhida, não.
 */
export function trocarTipo(f: Formulario, familia: Familia): Formulario {
  const padraoAnterior = String(TIPOS_DE_BANCO[f.familia].porta);
  const porta =
    f.porta.trim() === '' || f.porta.trim() === padraoAnterior
      ? String(TIPOS_DE_BANCO[familia].porta)
      : f.porta;
  return { ...f, familia, porta };
}

export type Problemas = Partial<Record<keyof Formulario, string>>;

/** O que falta, campo a campo, com os textos do desenho. */
export function problemasDoFormulario(f: Formulario, pedeSenha: boolean): Problemas {
  const p: Problemas = {};
  if (!f.nome.trim()) p.nome = 'Dê um nome para reconhecer a conexão.';
  const host = f.host.trim();
  if (!host) p.host = 'Informe o endereço do servidor.';
  else if (/^https?:\/\//i.test(host)) {
    p.host = 'Tire o “http://”: é só o endereço, como db.empresa.com.br.';
  } else if (/\s/.test(host)) p.host = 'O endereço não pode ter espaços.';
  const porta = Number(f.porta);
  if (!/^\d+$/.test(f.porta.trim()) || porta < 1 || porta > 65535) {
    p.porta = 'Porta entre 1 e 65535.';
  }
  if (!f.banco.trim()) p.banco = 'Informe o nome do banco.';
  if (!f.usuario.trim()) p.usuario = 'Informe o usuário.';
  if (pedeSenha && !f.senha) p.senha = 'Informe a senha. Ela vai direto para o cofre.';
  return p;
}

export function corpoParaCriar(f: Formulario): CriarConexao {
  return {
    nome: f.nome.trim(),
    familia: f.familia,
    host: f.host.trim(),
    porta: Number(f.porta),
    banco: f.banco.trim(),
    usuario: f.usuario.trim(),
    senha: f.senha,
    cenario: f.cenario,
  };
}

/** Editar: a senha só vai quando a pessoa a trocou. */
export function corpoParaEditar(f: Formulario, trocandoSenha: boolean): EditarConexao {
  return {
    nome: f.nome.trim(),
    host: f.host.trim(),
    porta: Number(f.porta),
    banco: f.banco.trim(),
    usuario: f.usuario.trim(),
    cenario: f.cenario,
    ...(trocandoSenha && f.senha ? { senha: f.senha } : {}),
  };
}
